import { type ReactNode, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData, useSearchParams } from '@remix-run/react';
import type { Tier as DreamTier, PricingVisibleSection, TPricingElement } from '@shared/dream-components';
import { currencyFormatter, PricingElement } from '@shared/dream-components';
import { produce } from 'immer';

import Text from '~/components/Text';
import { UpgradeContext } from '~/context/UpgradeContext';
import { UpgradeProvider } from '~/context/UpgradeProvider';
import usePricingDetails from '~/hooks/useLoaders/usePricingDetails';
import type { PricingDetails, Price as SwarmPrice, Tier as SwarmTier } from '~/interfaces/subscriptions';
import type { RootLoaderData } from '~/root';

import { buildGroupSummaryStrings, getActiveSelection } from './Pricing.helpers';
import PricingCheckout from './PricingCheckout';
import PricingSkeleton from './PricingSkeleton';

interface Props {
  node: TPricingElement;
  children: ReactNode | ReactNode[];
  onSuccess?: (ssoLoginUrl?: string) => void;
  successRedirectTo?: string;
  forceStripePaymentElement?: boolean;
  visibleSection?: PricingVisibleSection;
}

const mapTier = (
  tier: DreamTier,
  validTiers: Record<string, SwarmTier>,
  validPricesMap: Record<string, SwarmPrice>,
) => {
  if (!(tier.id in validTiers)) {
    return tier;
  }

  const liveTier = validTiers[tier.id];

  return {
    ...tier,
    redirect_url: liveTier.redirect_url,
    group_enabled: liveTier.group_enabled,
    group_minimum_quantity: liveTier.group_minimum_quantity,
    group_discount_percent: liveTier.group_discount_percent,
    // Drop website-builder prices the backend didn't return — e.g. a tier configured
    // with month + year in the builder but only year-eligible in the group context.
    prices: tier.prices
      .filter((price) => price.id in validPricesMap)
      .map((price) => {
        const livePrice = validPricesMap[price.id];

        return {
          ...price,
          amount_cents: livePrice.amount_cents,
          original_amount_cents: livePrice.original_amount_cents,
          currency: livePrice.currency,
          interval: livePrice.interval,
          cta: livePrice.cta,
          denominator: livePrice.denominator,
          features: livePrice.features || [],
          premium_offer: livePrice.premium_offer,
        };
      }),
  };
};

// Swap tiers out that might not be applicable to the current subscriber
// based on their subscription
const swapTiersInNode = (node: TPricingElement, pricingData: PricingDetails) => {
  const validPricesMap: Record<string, SwarmPrice> = {};
  const validGiftPricesMap: Record<string, SwarmPrice> = {};
  const validGroupPricesMap: Record<string, SwarmPrice> = {};

  // Make a hash of valid tiers downloaded on the client side
  const validTiersById: Record<string, SwarmTier> = (pricingData?.tiers || []).reduce(
    (acc, currentValue) => {
      acc[currentValue.id] = currentValue;

      // Map prices
      for (const price of currentValue.prices || []) {
        validPricesMap[price.id] = price;
      }
      return acc;
    },
    {} as Record<string, SwarmTier>,
  );

  // Make a hash of valid gift tiers downloaded on the client side
  const validGiftTiersById: Record<string, SwarmTier> = (pricingData?.gift_tiers || []).reduce(
    (acc, currentValue) => {
      acc[currentValue.id] = currentValue;

      // Map prices
      for (const price of currentValue.prices || []) {
        validGiftPricesMap[price.id] = price;
      }
      return acc;
    },
    {} as Record<string, SwarmTier>,
  );

  const validGroupTiersById: Record<string, SwarmTier> = (pricingData?.group_tiers || []).reduce(
    (acc, currentValue) => {
      acc[currentValue.id] = currentValue;

      for (const price of currentValue.prices || []) {
        validGroupPricesMap[price.id] = price;
      }
      return acc;
    },
    {} as Record<string, SwarmTier>,
  );

  return produce(node, (draft) => {
    draft.attrs.data = (draft.attrs.data || [])
      .filter((tier) => {
        // The free tier is a builder-injected card, never in the backend preview. When the
        // page is scoped to a direct-link offer's tiers, drop it too — otherwise it's the one
        // tier the offer filter can't reach.
        if (tier.id === 'free') return !pricingData.offer_tiers_only;
        return !!validTiersById[tier.id];
      })
      .map((tier) => mapTier(tier, validTiersById, validPricesMap));

    // Update node and evaluate gift tiers by looking up any
    // gift tiers based on the tiers chosen in the website builder
    // by the user
    // Example;
    // 1. User created 3 tiers
    // 2. Tier 1 and 2 has gifting turned on
    // 3. User selects tier 1 and 3 in website builder
    // 4. We display "Tier 1" as a Tier to gift, but not tier 2
    // 5. We omit "Tier 2" because the user has not selected that
    //    tier in the website builder
    draft.attrs.giftTiersData = draft.attrs.data
      .filter((tier) => {
        return validGiftTiersById[tier.id];
      })
      .map((tier) => mapTier(tier, validGiftTiersById, validGiftPricesMap));

    draft.attrs.groupTiersData = node.attrs.isGroupSubscriptionsEnabled
      ? draft.attrs.data
          .filter((tier) => validGroupTiersById[tier.id])
          .map((tier) => mapTier(tier, validGroupTiersById, validGroupPricesMap))
      : [];
  });
};

const ErrorBoundaryFallback = ({ message }: { message: string }) => (
  <div className="flex flex-col max-w-2xl py-8 space-y-2">
    <Text as="p" type="body" size="md" className="leading-tight tracking-normal text-center">
      {message}
    </Text>
  </div>
);

const Pricing = ({
  node,
  children,
  onSuccess,
  successRedirectTo,
  forceStripePaymentElement,
  visibleSection,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const publication = site.publication;
  const [searchParams] = useSearchParams();
  const hasOfferId = searchParams.has('offer_id');

  const {
    data: pricingData,
    isLoading: isPricingLoading,
    isError: hasPricingDataFetchError,
  } = usePricingDetails({ enabled: node.attrs.hasFakeData !== true });

  const renderableNode = useMemo(() => {
    if (!isPricingLoading && !hasPricingDataFetchError && pricingData && node.attrs.hasFakeData !== true) {
      return swapTiersInNode(node, pricingData);
    }
    return node;
  }, [isPricingLoading, hasPricingDataFetchError, pricingData, node, node.attrs.hasFakeData]);

  const activeTiers = useMemo(
    () => (renderableNode.attrs.data || []).filter((tier) => !!tier.enabled) || [],
    [renderableNode.attrs.data],
  );
  const activeGiftTiers = useMemo(() => {
    try {
      return (renderableNode.attrs.giftTiersData || []).filter((tier) => !!tier.enabled) || [];
    } catch {
      return [];
    }
  }, [renderableNode.attrs.giftTiersData]);
  const activeGroupTiers = useMemo(() => {
    try {
      return (renderableNode.attrs.groupTiersData || []).filter((tier) => !!tier.enabled) || [];
    } catch {
      return [];
    }
  }, [renderableNode.attrs.groupTiersData]);
  const isTermsOfServiceEnabled = !!site.viewable_site_version.settings.terms_of_service_enabled;
  const hasCustomTermsOfService = site.viewable_site_version.settings.terms_of_service;
  const customTermsOfServiceText = site.viewable_site_version.settings.terms_of_service_text || undefined;
  const isCollectingBillingAddress = renderableNode.attrs.collectBillingAddress || false;
  const isGiftingOnly = renderableNode.attrs.isGiftingOnly ?? false;

  if (hasPricingDataFetchError) {
    return null;
  }

  // When an offer is being applied, suppress rendering until pricing data is ready.
  // Without this, the component renders with the original (non-offer) prices from
  // the static page content and then flashes to the offer price once the fetch completes.
  if (isPricingLoading && (hasOfferId || node.attrs.isGroupSubscriptionsEnabled) && node.attrs.hasFakeData !== true) {
    return <PricingSkeleton node={renderableNode} />;
  }

  return (
    <ErrorBoundary fallbackRender={(props) => <ErrorBoundaryFallback message={props.error.message} />}>
      <UpgradeProvider
        giftTiers={activeGiftTiers}
        groupTiers={activeGroupTiers}
        tiers={activeTiers}
        isInternalCheckout={renderableNode.attrs.internalCheckout}
        isGiftingEnabled={renderableNode.attrs.isGiftingEnabled}
        isGiftingOnly={isGiftingOnly}
        recommendedTierId={renderableNode.attrs.recommendedTierId}
        recommendedPriceId={renderableNode.attrs.recommendedPriceId}
        nodeAttributes={renderableNode.attrs}
      >
        <UpgradeContext.Consumer>
          {(contextValue) => {
            const activeSelection = getActiveSelection(contextValue);

            const { groupSummary, minimumSeatsLabel } = buildGroupSummaryStrings(contextValue, t, i18n.language);

            const pricingTranslations = {
              'upgrade.tabs.gift.title': t('upgrade.tabs.gift.title'),
              'upgrade.tabs.group.title': t('upgrade.tabs.group.title'),
              'upgrade.tabs.individual.title': t('upgrade.tabs.individual.title'),
              'upgrade.tabs.gift.form.title': t('upgrade.tabs.gift.form.title', {
                price: contextValue?.selectedGiftPrice
                  ? currencyFormatter({
                      value: contextValue.selectedGiftPrice.amount_cents / 100,
                      currency: contextValue.selectedGiftPrice.currency,
                    })
                  : '-',
                interval: contextValue.selectedGiftPrice?.interval,
                tier_name: contextValue?.selectedGiftTier?.name,
              }),
              'upgrade.tabs.gift.form.purchaser_email': t('upgrade.tabs.gift.form.purchaser_email'),
              'upgrade.tabs.gift.form.purchaser_name': t('upgrade.tabs.gift.form.purchaser_name'),
              'upgrade.tabs.gift.form.recipient_email': t('upgrade.tabs.gift.form.recipient_email'),
              'upgrade.tabs.gift.form.message': t('upgrade.tabs.gift.form.message'),
              'upgrade.tabs.gift.form.message_placeholder': t('upgrade.tabs.gift.form.message_placeholder'),
              'upgrade.tabs.gift.form.start_at': t('upgrade.tabs.gift.form.start_at'),
              'upgrade.tabs.group_subs.description': t('upgrade.tabs.group.description', {
                name: publication?.name ?? '',
              }),
              'upgrade.tabs.group.per_seat_prefix': t('upgrade.tabs.group.per_seat_prefix'),
              'upgrade.tabs.group.checkout_cta': t('upgrade.tabs.group.checkout_cta'),
              'upgrade.tabs.group.form.purchaser_email': t('upgrade.tabs.group.form.purchaser_email'),
              'upgrade.tabs.group.form.group_name': t('upgrade.tabs.group.form.group_name'),
              'upgrade.tabs.group.form.seats': t('upgrade.tabs.group.form.seats'),
              'upgrade.tabs.group.form.minimum_seats': minimumSeatsLabel,
              'upgrade.tabs.group.form.summary': groupSummary,
              'upgrade.weekly.per_week': t('upgrade.weekly.per_week'),
              'upgrade.weekly.billed_as': t('upgrade.weekly.billed_as'),
            };

            return (
              <PricingCheckout
                node={renderableNode}
                fallback={<PricingSkeleton node={renderableNode} />}
                onSuccess={onSuccess}
                successRedirectTo={successRedirectTo}
                forceStripePaymentElement={forceStripePaymentElement}
              >
                {(checkout) =>
                  checkout.isSignup ? (
                    <PricingElement
                      visibleSection={visibleSection}
                      isCollectingBillingAddress={isCollectingBillingAddress}
                      element={renderableNode}
                      isInternalCheckout={renderableNode.attrs.internalCheckout}
                      selectedTier={contextValue.selectedTier}
                      onTierClick={contextValue.setSelectedTier}
                      selectedPrice={contextValue.selectedPrice}
                      onPriceClick={contextValue.setSelectedPrice}
                      isStripeLoading={false}
                      stripeSessionIds={{}}
                      email={contextValue.email}
                      onEmailChange={checkout.setEmail}
                      showTaxIdField={false}
                      taxId={contextValue.taxId}
                      onTaxIdChange={contextValue.setTaxId}
                      currentFormMode={contextValue.currentFormMode}
                      onCurrentFormModeChange={contextValue.setCurrentFormMode}
                      translations={pricingTranslations}
                      donationAmountCents={contextValue.donationAmountCents}
                      onDonationAmountCentsChange={contextValue.setDonationAmountCents}
                      isSubmitting={checkout.isSubmitting}
                      hasTermsOfService={isTermsOfServiceEnabled}
                      hasCustomTermsOfService={hasCustomTermsOfService}
                      customTermsOfServiceText={customTermsOfServiceText}
                      isLive
                    >
                      {children}
                    </PricingElement>
                  ) : (
                    <PricingElement
                      visibleSection={visibleSection}
                      key={contextValue.currentFormMode}
                      isCollectingBillingAddress={isCollectingBillingAddress}
                      element={renderableNode}
                      isInternalCheckout={renderableNode.attrs.internalCheckout}
                      selectedTier={activeSelection.tier}
                      onTierClick={activeSelection.setTier}
                      selectedPrice={activeSelection.price}
                      onPriceClick={activeSelection.setPrice}
                      isStripeLoading={contextValue.isStripeLoading}
                      email={contextValue.email}
                      onEmailChange={checkout.setEmail}
                      showTaxIdField={contextValue.showTaxIdField}
                      taxId={contextValue.taxId}
                      onTaxIdChange={contextValue.setTaxId}
                      donationAmountCents={contextValue.donationAmountCents}
                      onDonationAmountCentsChange={contextValue.setDonationAmountCents}
                      isSubmitting={checkout.isSubmitting}
                      translations={pricingTranslations}
                      currentFormMode={contextValue.currentFormMode}
                      onCurrentFormModeChange={contextValue.setCurrentFormMode}
                      groupName={contextValue.groupName}
                      onGroupNameChange={contextValue.setGroupName}
                      seatCount={contextValue.seatCount}
                      onSeatCountChange={contextValue.setSeatCount}
                      StripeCardElement={checkout.StripeCardElement}
                      StripePaymentElement={checkout.StripePaymentElement}
                      PaymentRequestButtonElement={checkout.PaymentRequestButtonElement}
                      isLive
                    >
                      {children}
                    </PricingElement>
                  )
                }
              </PricingCheckout>
            );
          }}
        </UpgradeContext.Consumer>
      </UpgradeProvider>
    </ErrorBoundary>
  );
};

export default Pricing;
